/**
 * SphereCanvas — montagem segura das cenas WebGL (hero e Mission Control).
 *
 * - WebGL indisponível, erro no renderer (ErrorBoundary) ou contexto perdido →
 *   fallback estático: o PNG `hero-sphere.png` em resolução original (hero) ou o
 *   desenho SVG/CSS do núcleo orbital (Mission Control).
 * - A cena só anima com movimento permitido: preferência do usuário, sem
 *   `prefers-reduced-motion`, aba visível e região na viewport (fora disso o
 *   R3F fica em `frameloop="demand"`: um quadro estático, sem RAF).
 * - Camadas decorativas: `aria-hidden` e `pointer-events: none`. Texto e números
 *   ficam em HTML fora do canvas.
 * - Desmontar o componente desmonta o Canvas do R3F, que libera o renderer, o
 *   RAF e os observers (seguro em StrictMode).
 */
import { Component, Suspense, lazy, useCallback, useRef, useState } from 'react';
import heroSphereUrl from '@/assets/sphere/hero-sphere.png';
import { useAnimationAllowed } from './motion';
import { MISSION_SPEED } from './missionModel';

// Um único carregamento do chunk das cenas (three + R3F), compartilhado pelas duas.
let cenas = null;
const carregarCenas = () => {
  cenas = cenas ?? import('./three/SphereScenes');
  return cenas;
};
const HeroScene = lazy(() => carregarCenas().then((m) => ({ default: m.HeroScene })));
const MissionScene = lazy(() => carregarCenas().then((m) => ({ default: m.MissionScene })));

export const HERO_FALLBACK_SRC = heroSphereUrl;

/** true se o navegador cria um contexto WebGL (webgl2 ou webgl). */
export function webglDisponivel() {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false;
    if (!window.WebGLRenderingContext && !window.WebGL2RenderingContext) return false;
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

class ErroDoRenderer extends Component {
  constructor(props) {
    super(props);
    this.state = { erro: false };
  }

  static getDerivedStateFromError() {
    return { erro: true };
  }

  componentDidCatch(err) {
    // Falha do renderer não derruba a tela: cai no fallback estático.
    console.warn('[sphere] renderer WebGL indisponível — usando imagem estática.', err?.message || err);
  }

  render() {
    return this.state.erro ? this.props.fallback : this.props.children;
  }
}

function useContextoPerdido() {
  const [perdido, setPerdido] = useState(false);
  const onCreated = useCallback((state) => {
    const el = state?.gl?.domElement;
    if (!el?.addEventListener) return;
    el.addEventListener('webglcontextlost', (e) => { e.preventDefault(); setPerdido(true); }, { once: true });
  }, []);
  return [perdido, onCreated];
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

export function HeroSphere({ className = 'sp-hero-art' }) {
  const ref = useRef(null);
  const { allowed } = useAnimationAllowed(ref);
  const [gl] = useState(webglDisponivel);
  const [perdido, onCreated] = useContextoPerdido();
  const fallback = (
    <img src={heroSphereUrl} alt="" width={1536} height={1024} decoding="async" data-testid="hero-fallback" />
  );
  const usar3d = gl && !perdido;
  return (
    <div ref={ref} className={className} aria-hidden="true" data-renderer={usar3d ? 'webgl' : 'imagem'} data-animando={usar3d && allowed ? 'sim' : 'nao'}>
      {usar3d ? (
        <ErroDoRenderer fallback={fallback}>
          <Suspense fallback={fallback}>
            <HeroScene animate={allowed} onCreated={onCreated} />
          </Suspense>
        </ErroDoRenderer>
      ) : fallback}
    </div>
  );
}

// ─── Mission Control (núcleo + órbitas + cubos) ──────────────────────────────

/** Desenho estático (fallback): anéis, cubos e núcleo — mesmo do pacote. */
export function OrbitFallback({ state }) {
  const cubos = [[74, 79], [119, 191], [224, 43], [347, 111], [307, 224]];
  const tinta = state === 'error' ? 'var(--sp-danger)' : state === 'approval' ? 'var(--sp-warning)' : 'var(--sp-accent)';
  return (
    <div className="sp-orbit-fallback" data-testid="orbit-fallback">
      <svg viewBox="0 0 420 250" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <defs>
          <radialGradient id="sp-core-static" cx="35%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#3a8df6" />
            <stop offset="55%" stopColor="#091d50" />
            <stop offset="85%" stopColor="#122c79" />
            <stop offset="100%" stopColor="#837cff" />
          </radialGradient>
        </defs>
        {[0, 1, 2, 3].map((i) => (
          <ellipse key={i} cx="210" cy="140" rx={176 - i * 13} ry={67 - i * 5} transform={`rotate(${-10 + i * 7} 210 140)`}
            fill="none" stroke="#3175dc" strokeWidth=".8" opacity=".5" />
        ))}
        <circle cx="210" cy="132" r="72" fill="url(#sp-core-static)" stroke={tinta} strokeWidth="1.5" />
        {cubos.map(([x, y], i) => (
          <g key={i}>
            <path d={`M${x} ${y - 12}l12 7v15l-12 7-12-7v-15Z`} fill="#176ad766" stroke="#55baff" strokeWidth="1.4" />
            <path d={`M${x - 12} ${y - 5}l12 7 12-7M${x} ${y + 2}v15M${x} ${y - 12}v14`} fill="none" stroke="#9abfff" strokeWidth=".85" />
          </g>
        ))}
      </svg>
    </div>
  );
}

export function MissionOrbit({ state = 'idle', label = 'Agentes em execução', value, hint }) {
  const ref = useRef(null);
  const { allowed } = useAnimationAllowed(ref);
  const [gl] = useState(webglDisponivel);
  const [perdido, onCreated] = useContextoPerdido();
  const usar3d = gl && !perdido;
  const fallback = <OrbitFallback state={state} />;
  const anima = usar3d && allowed && (MISSION_SPEED[state] ?? 0) > 0;
  return (
    <div ref={ref} className="sp-orbit" data-renderer={usar3d ? 'webgl' : 'svg'} data-animando={anima ? 'sim' : 'nao'}>
      <div className="sp-orbit-canvas" aria-hidden="true">
        {usar3d ? (
          <ErroDoRenderer fallback={fallback}>
            <Suspense fallback={fallback}>
              <MissionScene state={state} animate={allowed} onCreated={onCreated} />
            </Suspense>
          </ErroDoRenderer>
        ) : fallback}
      </div>
      <div className="sp-orbit-label">
        {label.split(' em ').length > 1 ? (<>{label.split(' em ')[0]}<br />em {label.split(' em ')[1]}</>) : label}
        <strong className="sp-num">{value}</strong>
        {hint && <small>{hint}</small>}
      </div>
    </div>
  );
}
